import type { PlayErrorDetails } from '@empathybroker/tsclient-play/dist/models/play-error-details'
import type { ApiError } from '@empathyco/playboard-core-domain'
import type { BadRequestMessage, ErrorMessage, ErrorsMessages } from '../models'
import { getCurrentInstance } from 'vue'
import { compileMessage } from '../utils/messages.utils'

export interface ApiErrorComposable {
  getErrorMessage: (error: ApiError) => ErrorMessage
  isApiError: (error: any) => boolean
  isConflictError: (error: ApiError) => boolean
}

export interface ApiErrorComposableProps {
  messages?: Partial<ErrorsMessages>
}

export function useApiError(props: ApiErrorComposableProps = {}): ApiErrorComposable {
  const ctx = getCurrentInstance()

  function isApiError(error: any): boolean {
    return !!error.status
  }

  function getErrorMessageObj(error: ApiError): ErrorMessage {
    return {
      title: getErrorTitle(error),
      message: getErrorMessage(error),
    }
  }

  function getErrorTitle(error: ApiError): string {
    let result = error.title
    if (!result) {
      const statusErrorMessage = getI18nErrorMessage(error.status)
      if (statusErrorMessage) {
        result = statusErrorMessage.title
      } else {
        const defaultErrorMessage = getI18nErrorMessage('default')
        if (defaultErrorMessage) {
          result = compileMessage(defaultErrorMessage.title, {
            code: error.status?.toString(),
          })
        }
      }
    }
    return result ?? ''
  }

  function getI18nErrorMessage(key: keyof ErrorsMessages): ErrorMessage | undefined {
    if (props.messages && key in props.messages) {
      return props.messages[key]
    }

    const errorMessages = ctx?.proxy.$epb.messages.errors as ErrorsMessages
    return errorMessages?.[key] as ErrorMessage
  }

  function getErrorMessage(error: ApiError): string {
    let result = error.message
    if (!result) {
      const statusErrorMessage = getI18nErrorMessage(error.status)
      if (statusErrorMessage) {
        if (error.status === 400) {
          result = getBadRequestErrorMessage(error, statusErrorMessage as BadRequestMessage)
        } else {
          result = statusErrorMessage.message
        }
      } else {
        const defaultErrorMessage = getI18nErrorMessage('default')
        if (defaultErrorMessage) {
          result = defaultErrorMessage.message
        }
      }
    }
    return result ?? ''
  }

  function getBadRequestErrorMessage(error: ApiError, { message }: BadRequestMessage): string {
    let result = message
    const playErrorDetails = error.details as PlayErrorDetails // TODO Remove API specific treatment from this composable (https://github.com/empathyco/playboard/pull/564#discussion_r1553098199)
    if (playErrorDetails?.fields?.length) {
      const messages = playErrorDetails.fields.reduce<Array<string>>((partialResult, field) => {
        return [
          ...partialResult,
          ...(field.message ? [field.message] : field.reasons.map(reason => reason.message)),
        ]
      }, [])
      if (messages.length) {
        result = messages.join('<br>') // TODO Avoid formatting message from this composable (https://github.com/empathyco/playboard/pull/564#discussion_r1553103223)
      }
    }
    return result
  }

  function isConflictError(error: ApiError): boolean {
    return error.status === 409 && 'conflictingElements' in error
  }

  return {
    getErrorMessage: getErrorMessageObj,
    isApiError,
    isConflictError,
  }
}
